// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { messageBoxHandler } from '@shared/utils/AppHandlers';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { Intervals } from '@shared/utils/Instruments/Intervals';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { MathUtils } from '@shared/utils/MathUtils';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderEditBaseUtils } from '@shared/utils/Trading/OrderEditBaseUtils';
import { DynProperty } from '../../DynProperty';
import { BaseSettings } from '../../Settings/BaseGeneralSettingsWrapper';
import { Account } from '../Account';
export class PositionSizingEdit {
    constructor() {
        this.account = null; // аккаунт панели для определения валюты и AvailableFunds
        this.instrument = null; // инструмент для настройки нумерика
        this.productType = null;
        this.isVisible = null; // отображаются ли контролы PositionSizing-а (включена ли кнопка калькулятора)
        this.isPercentMode = true; // в контроле Account risk отображаются проценты, иначе Cash
        this.errorMsgShowing = false; // отображается ли окно с ошибкой по поводу некорректных параметров
        this.userValue = null; // значение в контроле Account risk
        this.riskValue = null; // значение в строке Cash / Percentage risk
        this.isMarketOrder = null; // true, если OrderType == 1 (Market)
        this.slvalue = null; // значение в SL нумерике
        this.price = null; // цена (лимит, маркет, стоп и тд)
        this.isBuy = null; // сторона ордера
    }
    get Price() {
        if (this.isMarketOrder) {
            return this.instrument.Level1[this.IsBuy ? 'GetAsk' : 'GetBid'](this.account);
        }
        else if (this.IsTrStop) {
            return this.price * this.instrument.getPointSize(null);
        }
        else {
            return this.price;
        }
    }
    set Price(price) {
        this.price = price;
    }
    get SLValue() {
        return this.slvalue;
    }
    set SLValue(slvalue) {
        this.slvalue = slvalue;
    }
    get SLOffset() {
        if (this.OffsetMode === null) {
            const ins = this.instrument;
            return ins.calculateTicks(this.Price, this.SLValue - this.Price);
        }
        else {
            return this.SLValue;
        }
    }
    get SLPrice() {
        // #115352
        const ins = this.instrument;
        if (this.OffsetMode !== null /* && this.SLTPinOffset */ && !this.IsTrStop) {
            const tickSign = this.IsBuy ? -1 : 1;
            let sl = this.SLValue;
            if (this.OffsetModeInPoints) {
                sl = InstrumentUtils.ConvertPointsToTicks(ins, sl);
            } // #114393
            return ins.calculatePrice(this.Price, sl * tickSign);
        }
        if (this.IsTrStop) {
            return ins.calculatePrice(this.Price, this.SLValue, true);
        }
        else {
            return this.SLValue;
        }
    }
    get OffsetMode() {
        return this.offsetMode;
    }
    set OffsetMode(offsetMode) {
        this.offsetMode = offsetMode;
    }
    get SLTPinOffset() {
        return BaseSettings.isSlTpInOffset;
    }
    get OffsetModeInPoints() {
        return BaseSettings.offsetType === OffsetModeViewEnum.Points;
    }
    get RiskPerShare() {
        var _a, _b;
        const result = Math.abs(this.Price - this.SLPrice);
        return result * ((_b = (_a = this.instrument) === null || _a === void 0 ? void 0 : _a.GetTickCost()) !== null && _b !== void 0 ? _b : 1);
    }
    get IsBuy() {
        return this.isBuy;
    }
    set IsBuy(isBuy) {
        this.isBuy = isBuy;
    }
    get IsTrStop() {
        return this.isTrStop;
    }
    set IsTrStop(isTrStop) {
        this.isTrStop = isTrStop;
    }
    update(updateData) {
        let parameterChanged = false;
        const dp = updateData.dp;
        if (dp) {
            parameterChanged = this.updateFromDynProperty(dp) || parameterChanged;
        }
        const dataDict = updateData.newTradingDataDict;
        if (!dataDict) {
            return parameterChanged;
        }
        const acc = dataDict.account;
        if (acc && (!this.account || this.account.AcctNumber != acc.AcctNumber)) {
            this.account = acc;
            this.riskValue = this.recalcOtherRisk();
            parameterChanged = true;
        }
        const ins = dataDict.instrument;
        if (ins && (!this.instrument || this.instrument.GetInteriorID() != ins.GetInteriorID())) {
            this.instrument = ins;
            parameterChanged = true;
        }
        const pT = dataDict.productType;
        if (pT != null && pT !== this.productType) {
            this.productType = pT;
            parameterChanged = true;
        }
        return parameterChanged;
    }
    updateFromDynProperty(dp) {
        let parameterChanged = false;
        let newVal = dp.positionSizingIsVisible;
        if (newVal !== this.isVisible) {
            this.isVisible = newVal;
            parameterChanged = true;
        }
        newVal = dp.riskModeBtnText == '%';
        if (newVal !== this.isPercentMode) {
            this.isPercentMode = newVal;
            parameterChanged = true;
        }
        newVal = dp.value;
        if (newVal !== this.userValue) {
            this.userValue = newVal;
            this.riskValue = this.recalcOtherRisk();
            parameterChanged = true;
        }
        if (dp.availableFundsChanged) {
            parameterChanged = true;
        }
        return parameterChanged;
    }
    getRawValue() {
        return this.userRiskValue;
    }
    getDynProperty() {
        const dp = new DynProperty(OrderEditBaseUtils.POSITION_SIZING_PARAM);
        dp.localizationKey = 'positionSizeCalculator.accountRisk';
        dp.type = DynProperty.POSITION_SIZING;
        dp.positionSizingIsVisible = this.isVisible || false;
        const acc = this.account;
        const currency = acc ? acc.BaseCurrency : '';
        let riskMode = '%';
        const isPercent = this.isPercentMode;
        if (acc && !isPercent) {
            riskMode = currency;
        }
        dp.riskModeBtnText = riskMode;
        dp.currencyRiskModeSuffix = currency;
        const ins = this.instrument;
        const assetBalance = acc ? acc.GetAssetBalanceCorrect(ins || null, this.isBuy) : null;
        const availableFunds = isPercent
            ? null
            : Account.GetAccountFeature(AccountFeature.AvailableFunds, acc, assetBalance);
        const asset = assetBalance ? assetBalance.Asset : null;
        const intrvls = Intervals.GetIntervalsFromInstrument(ins, NumericUtils.MAXVALUE);
        if (ins) {
            dp.increment = dp.minimalValue = isPercent ? 0.01 : asset.MinChange;
            dp.maximalValue = isPercent ? 100 : availableFunds;
            dp.decimalPlaces = isPercent ? 2 : asset.Point;
        }
        if (intrvls) {
            dp.Intervals = intrvls;
        }
        dp.value = this.userValue || 2;
        if (dp.value === null || isNaN(dp.value)) {
            dp.value = dp.minimalValue;
        }
        const value = this.riskValue;
        const formattedValue = isPercent && acc ? acc.formatPrice(value) : PriceFormatter.formatPrice(value, 2);
        dp.riskValue = value;
        dp.riskValueFormatted = formattedValue + (isPercent ? '' : ' %'); // second row calculated risk
        return dp;
    }
    recalcOtherRisk() {
        const accRiskValue = this.userValue;
        const isPercentMode = this.isPercentMode;
        const acc = this.account;
        if (!acc)
            return NaN;
        const availableFunds = Account.GetAccountFeature(AccountFeature.AvailableFunds, acc, acc.assetBalanceDefault);
        let value;
        if (isPercentMode) {
            value = (availableFunds * accRiskValue) / 100;
        }
        else {
            value = availableFunds != 0 ? (accRiskValue * 100) / availableFunds : NaN;
        }
        return value;
    }
    calculateQty(price, slPrice, inLots, isBuy, offsetMode, isMarketOrder, isTrStop) {
        // Calculated quantity field value
        if (!this.isVisible) {
            return null;
        }
        if (isNaN(price)) {
            price = 0;
        }
        const acc = this.account;
        const ins = this.instrument;
        if (!acc || !ins)
            return null;
        this.riskValue = this.recalcOtherRisk();
        this.isMarketOrder = isMarketOrder;
        this.Price = price;
        this.SLValue = slPrice;
        this.OffsetMode = offsetMode;
        this.IsBuy = isBuy;
        this.IsTrStop = isTrStop;
        const lotSize = ins.getLotSize();
        const crossPrice = this.getCrossPrice();
        const riskPerTrade = this.getUserValueInCash() * crossPrice;
        const riskPerShare = this.RiskPerShare;
        let value = riskPerShare
            ? MathUtils.TruncateDouble(riskPerTrade / riskPerShare, InstrumentUtils.MAX_PRECISION_ON_SERVER)
            : NaN;
        if (inLots) {
            value = MathUtils.TruncateDouble(value / lotSize, ins.getLotStepPrecision(this.productType, this.account));
        }
        else {
            const p = ins.getLotStep(this.productType, this.account) * lotSize;
            const d = p * Math.floor(value / p);
            if (!MathUtils.equalToEpsilon(d, value)) {
                value -= value % p;
            }
        }
        if (isNaN(value) && !this.errorMsgShowing && this.isVisible) {
            this.errorMsgShowing = true;
            messageBoxHandler.Show(Resources.getResource('screen.error.title'), Resources.getResource('positionSizeCalculator.UnableToCalc'), messageBoxHandler.msgType.Error, function () {
                this.errorMsgShowing = false;
            }.bind(this), null, false, true);
        }
        return value;
    }
    getUserValueInCash() {
        // переводит Account risk в деньги если задано в процентах
        const acc = this.account;
        if (!acc)
            return null;
        let result = this.userValue;
        if (this.isPercentMode) {
            const availableFunds = Account.GetAccountFeature(AccountFeature.AvailableFunds, acc, acc.assetBalanceDefault);
            result = (availableFunds * this.userValue) / 100;
        }
        return result;
    }
    getCrossPrice() {
        // кросс курс из валюты аккаунта в валюту котирования инструмента;
        const acc = this.account;
        const ins = this.instrument;
        if (!acc || !ins)
            return null;
        const side = this.IsBuy ? OperationType.Buy : OperationType.Sell;
        const crossPrice = acc.DataCache.CrossRateCache.GetCrossPriceInsSideExp2(acc, ins, side, true);
        return crossPrice;
    }
    getAvailableFundsUpdateData() {
        const dp = this.getDynProperty();
        const updateData = {};
        dp.availableFundsChanged = true;
        updateData[OrderEditBaseUtils.POSITION_SIZING_PARAM] = dp;
        return { dpDict: updateData };
    }
}
// неправильный подход взятый из таблицы расписанной в документации (https://docs.google.com/document/d/1xViY9yArfT-Cn-POlfuWT9T6V6ZoUFxZ3gr912OGexo п.5)
// -> не работает при переходе в разные интервалы переменного тик сайза
// этот метод пока оставлю, но удалим если новых подход (всегда считать RiskPerShare в абсолютных прайсах) себя оправдает
// public getRiskPerShare ()
// {
//     let acc = this.account,
//         ins = this.instrument
//     if (!acc || !ins) return
//     let isBuy = this.IsBuy,
//         price = this.Price,
//         sl = this.SLPrice,
//         trSett = GeneralSettings.TradingDefaults,
//         offsetMode = trSett.ShowOffsetIn,
//         sltpOffsetMode = BaseSettings.isSlTpOffsetMode,
//         inPoints = offsetMode === OffsetModeViewEnum.Points,
//         isTickCost_TickSize = ins.QuotingType === QuotingType.TickCost_TickSize,
//         slPrice = sltpOffsetMode ? ins.CalculatePrice(price, sl * (isBuy ? -1 : 1)) : sl,
//         pointSize = ins.GetPointSize(slPrice),
//         result = 0
//     if (sltpOffsetMode)                                         // При Set SL/TP values in offset = true
//     {
//         if (inPoints)                                           // и Show offset in = Points:
//         {
//             result = sl
//             if (isTickCost_TickSize)
//                 result = sl * ins.FuturesTickCoast / pointSize  // Risk per share = SL offset*tickCost/tickSize
//         }
//         else                                                    // иначе если в тиках
//         {
//             if (trSett.IsTicksFractionalForForex())    // Для типа котирования tickCost/tickSze также как и для оффсета в тиках
//                 result = sl * 10 * pointSize                    // Risk per share = SL offset * 10 * tick size или Risk per share = (SL offset + SL limit offset) * 10 * tick size если используется Use Stop limit instead of Stop
//             else
//                 result = sl * (isTickCost_TickSize ?            // Для типа котирования tickCost/tickSze: Risk per share = SL offset * tickCost
//                     ins.FuturesTickCoast :                      // При Set SL/TP values in offset = true и Show offset in = Ticks:
//                     pointSize)                                  // Risk per share = SL offset * tick size или Risk per share = (SL offset + SL limit offset) * tick size если используется Use Stop limit instead of Stop
//         }
//     }
//     else
//     {                                                           // При Set SL/TP values in offset = false:
//         result = Math.abs(price - sl)                           // Risk per share = abs(Price - SL price) или Risk per share = abs(Price - SL limit price) если используется Use Stop limit instead of Stop
//         if (isTickCost_TickSize)                                // Для типа котирования tickCost/tickSze:
//             result *= ins.FuturesTickCoast / pointSize          // Risk per share = abs(Price - SL price)*tickCost/tickSize
//     }
//     return result
// }
